class FacebookPage extends HTMLElement {
    private pageUrl: string | null = null;
    /**
     * This HTML element is used to display the status of the Facebook page loading if the Facebook SDK is not loaded yet or fails.
     */
    private statusContainer: HTMLElement;
    private fbWidget: HTMLElement | null = null;
    private fallbackLink: HTMLAnchorElement;
    private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    private boundOnWindowResize: () => void;
    /**
     * Watches for iframes injected by the FB SDK and stamps tabindex="-1" on
     * them immediately so keyboard users cannot tab into the embed's unlabeled
     * controls. aria-hidden on fbWidget handles the AT side; this handles the
     * keyboard side without blocking mouse/pointer interaction (which inert
     * would also block).
     */
    private widgetObserver: MutationObserver | null = null;

    private static sdkLoaded: boolean = false;
    private static sdkLoading: boolean = false;
    private static sdkCallbacks: Array<() => void> = [];
    private static readonly FB_SDK_VERSION = 'v24.0';

    public constructor() {
        super();
        this.className = 'facebook-page-container';
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', 'Facebook page feed');

        this.statusContainer = document.createElement('div');
        this.statusContainer.className = 'facebook-page-status';
        this.appendChild(this.statusContainer);

        this.fallbackLink = document.createElement('a');
        this.fallbackLink.className = 'sr-only sr-only-focusable';
        this.fallbackLink.target = '_blank';
        this.fallbackLink.rel = 'noopener noreferrer';
        this.fallbackLink.textContent = 'View on Facebook (opens in new tab)';
        this.appendChild(this.fallbackLink);

        // Store bound reference for proper cleanup in disconnectedCallback
        this.boundOnWindowResize = this.onWindowResize.bind(this);
    }

    public connectedCallback(): void {
        this.loadFacebookSDK(() => {
            if (this.pageUrl) {
                this.loadPage();
            }
        });
    }

    static get observedAttributes(): string[] {
        return ['page-url'];
    }

    public attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
        if (name !== 'page-url' || newValue === oldValue) {
            return;
        }
        this.pageUrl = newValue;
        if (this.fallbackLink) {
            this.fallbackLink.href = newValue ?? '';
            this.fallbackLink.hidden = !newValue;
            this.fallbackLink.textContent = `View ${FacebookPage.pageHandleFromUrl(newValue)} on Facebook (opens in new tab)`;
        }
        if (FacebookPage.sdkLoaded) {
            this.loadPage();
        }
    }

    public disconnectedCallback(): void {
        this.widgetObserver?.disconnect();
        this.widgetObserver = null;
        if (this.fbWidget && this.fbWidget.parentNode === this) {
            this.removeChild(this.fbWidget);
            this.fbWidget = null;
        }
        if (this.statusContainer && this.statusContainer.parentNode === this) {
            this.removeChild(this.statusContainer);
        }
        if (this.fallbackLink && this.fallbackLink.parentNode === this) {
            this.removeChild(this.fallbackLink);
        }
        window.removeEventListener('resize', this.boundOnWindowResize);
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }
    }

    private onWindowResize(): void {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        const height = Math.max(this.fbWidget?.clientHeight ?? 0, 530);
        this.resizeTimeout = setTimeout(() => {
            if (this.fbWidget) {
                window?.FB?.XFBML?.parse();
                this.fbWidget.style.minHeight = `${height}px`;
            }
        }, 800);
    }

    private renderFacebookRootElement() {
        const fbRoot = document.createElement('div');
        fbRoot.id = 'fb-root';
        document.body.appendChild(fbRoot);
    }

    private generateFacebookSdkScriptElement(errorCallback: () => void): HTMLScriptElement {
        const script = document.createElement('script');
        script.src = `https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=${FacebookPage.FB_SDK_VERSION}`;
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.onerror = errorCallback;
        return script;
    }

    private generateBlockquoteElement(pageUrl: string): HTMLElement {
        const blockquote = document.createElement('blockquote');
        blockquote.setAttribute('cite', pageUrl);
        blockquote.className = 'fb-xfbml-parse-ignore';
        const link = document.createElement('a');
        link.href = pageUrl;
        link.textContent = 'Facebook';
        blockquote.appendChild(link);

        return blockquote;
    }

    private static pageHandleFromUrl(url: string | null): string {
        if (!url) {
            return 'this page';
        }
        try {
            const pathname = new URL(url).pathname.replace(/\/$/, '');
            const handle = pathname.split('/').filter(Boolean).pop();
            return handle ?? 'this page';
        } catch {
            return 'this page';
        }
    }

    private loadFacebookSDK(callback: () => void): void {
        if (FacebookPage.sdkLoaded) {
            callback();
            return;
        }

        FacebookPage.sdkCallbacks.push(callback);

        if (FacebookPage.sdkLoading) {
            return;
        }
        FacebookPage.sdkLoading = true;

        // listen for window resize to update the height of the Facebook widget
        window.addEventListener('resize', this.boundOnWindowResize);

        if (!document.getElementById('fb-root')) {
            this.renderFacebookRootElement();
        }

        // Another loader (the social feed widget) may already have initialized the SDK with its own appId and version.
        // Calling FB.init again would override those settings, so reuse the SDK as it is.
        if (window.FB) {
            FacebookPage.sdkLoaded = true;
            FacebookPage.sdkLoading = false;
            FacebookPage.sdkCallbacks.forEach((cb) => cb());
            FacebookPage.sdkCallbacks = [];
            return;
        }

        window.fbAsyncInit = () => {
            try {
                window.FB?.init({
                    xfbml: true,
                    version: FacebookPage.FB_SDK_VERSION,
                });
                FacebookPage.sdkLoaded = true;
            } catch {
                FacebookPage.sdkLoaded = false;
            } finally {
                FacebookPage.sdkLoading = false;
                FacebookPage.sdkCallbacks.forEach((cb) => cb());
                FacebookPage.sdkCallbacks = [];
            }
        };

        const script = this.generateFacebookSdkScriptElement(() => {
            FacebookPage.sdkLoading = false;
            this.statusContainer.innerHTML = 'Failed to load Facebook SDK.';
        });

        // determine where to insert the script
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }
    }

    private loadPage(): void {
        if (!this.pageUrl) {
            this.statusContainer.innerHTML = 'No Facebook page URL provided.';
            return;
        }

        if (!FacebookPage.sdkLoaded) {
            this.statusContainer.innerHTML = 'Waiting for Facebook SDK...';
            return;
        }

        this.statusContainer.innerHTML = 'Loading Facebook page...';

        if (this.fbWidget && this.fbWidget.parentNode === this) {
            this.removeChild(this.fbWidget);
        }

        this.fbWidget = document.createElement('div');
        this.fbWidget.className = 'fb-page';
        // aria-hidden removes the embed from the AT — SR users reach Facebook
        // via the sr-only fallback link instead of navigating unlabeled iframe
        // content. tabindex="-1" on each SDK-injected iframe (applied by
        // widgetObserver below) closes the keyboard tab-through path without
        // blocking mouse/pointer interaction.
        this.fbWidget.setAttribute('aria-hidden', 'true');
        this.fbWidget.setAttribute('data-href', this.pageUrl);
        this.fbWidget.setAttribute('data-tabs', 'timeline');
        this.fbWidget.setAttribute('data-width', '500');
        this.fbWidget.setAttribute('data-height', '530');
        this.fbWidget.setAttribute('data-small-header', 'false');
        this.fbWidget.setAttribute('data-adapt-container-width', 'true');
        this.fbWidget.setAttribute('data-hide-cover', 'true');
        this.fbWidget.setAttribute('data-show-facepile', 'true');
        this.fbWidget.setAttribute('data-lazy', 'true');

        const blockquote = this.generateBlockquoteElement(this.pageUrl);
        this.fbWidget.appendChild(blockquote);
        this.appendChild(this.fbWidget);

        this.statusContainer.innerHTML = '';
        this.widgetObserver?.disconnect();
        this.widgetObserver = new MutationObserver(() => {
            const iframes = this.fbWidget?.querySelectorAll<HTMLIFrameElement>('iframe');
            if (iframes?.length) {
                iframes.forEach((iframe) => iframe.setAttribute('tabindex', '-1'));
                this.widgetObserver?.disconnect();
                this.widgetObserver = null;
            }
        });
        this.widgetObserver.observe(this.fbWidget, { childList: true, subtree: true });
        if (window.FB?.XFBML) {
            window.FB.XFBML.parse(this);
        } else {
            console.error('window.FB or window.FB.XFBML is not available');
        }
    }
}

if (!customElements.get('facebook-page')) {
    customElements.define('facebook-page', FacebookPage);
}
export default FacebookPage;
